/**
 * Μετάπτωση ενός καταστήματος σε δικούς μας κωδικούς (§13.19.Ε, §11.3).
 *
 * ΑΥΤΟ ΤΟ ΠΑΡΑΘΥΡΟ ΕΙΝΑΙ Η ΜΟΝΗ ΑΝΘΡΩΠΙΝΗ ΠΥΛΗ ΠΡΙΝ ΑΠΟ ΜΙΑ ΕΝΕΡΓΕΙΑ ΠΟΥ ΔΕΝ
 * ΑΝΑΙΡΕΙΤΑΙ. Το `POST /catalog/ingest` είναι ΟΛΙΚΗ ΑΝΤΙΚΑΤΑΣΤΑΣΗ: μετά από
 * αυτό, το προηγούμενο μενού δεν υπάρχει στο efood και δεν έχουμε καμία κλήση
 * που να το επαναφέρει (§13.19.ΙΔ — επαναφορά μόνο με νέο ingest από το
 * αρχειοθετημένο στιγμιότυπο S0, με ΝΕΟΥΣ πάλι κωδικούς).
 *
 * Γι' αυτό: κάθε απώλεια θέλει ρητό τσεκάρισμα, και το όνομα του καταστήματος
 * πρέπει να ΠΛΗΚΤΡΟΛΟΓΗΘΕΙ. Ένα «Είστε σίγουρος;» δεν διαβάζεται· ένα όνομα που
 * πρέπει να γραφτεί, διαβάζεται.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, Images, Loader2, Rocket, ShieldAlert, X,
} from 'lucide-react';
import {
  MigrationPreview, MigrationResult, MigrationStep, RunProgress,
} from '../../services/platformCatalogApi';
import { fmtDateTime, runStatusLabel, runWatchStuck } from './catalogUi';

interface Props {
  storeId: number;
  storeName: string;
  preview: MigrationPreview | null;
  loadingPreview: boolean;
  running: boolean;
  result: MigrationResult | null;
  /**
   * Η εκτέλεση στο παρασκήνιο (§11.9): η μετάπτωση απαντά **202** και συνεχίζει
   * μόνη της — ingest + αναμονή 6-20΄΄ + νέα εισαγωγή + re-key δεν χωρούν σε ένα
   * αίτημα HTTP. Εδώ φαίνεται η κατάσταση, ανανεωμένη κάθε 3΄΄.
   */
  progress?: RunProgress | null;
  /** Μόνιμο μήνυμα από 409/403/500 — ποτέ toast. */
  error: string | null;
  onClose: () => void;
  /**
   * Η ρητή έξοδος προς το «Ιστορικό» όσο η εκτέλεση τρέχει στο παρασκήνιο.
   * Χωρίς αυτήν, το παράθυρο έλεγε «δες το Ιστορικό» και ταυτόχρονα δεν άφηνε κανέναν
   * να πάει εκεί.
   */
  onGoToHistory?: () => void;
  onReloadPreview: () => void;
  onConfirm: (confirmText: string) => void;
}

const STEP_LABELS: Record<MigrationStep['step'], string> = {
  gates: 'Πύλες εξερχόμενης εγγραφής',
  lock: 'Κλείδωμα καταστήματος',
  pre_image: 'Φρέσκια εικόνα efood (S0 — η μόνη εφεδρεία)',
  build: 'Χτίσιμο του payload',
  integrity: 'Έλεγχος ακεραιότητας',
  ingest: 'POST /catalog/ingest — ΜΗ ΑΝΑΣΤΡΕΨΙΜΟ',
  poll: 'Αναμονή μέχρι να φανούν οι δικοί μας κωδικοί',
  import: 'Εισαγωγή του νέου καταλόγου',
  rekey: 'Μεταφορά συνδέσμων/διδύμων (re-key)',
  settings: 'Ρυθμίσεις καταστήματος (addressing = δικοί μας κωδικοί)',
  id_churn: 'Αναφορά αλλαγής κωδικών (id churn)',
};

const STEP_ORDER: Array<MigrationStep['step']> = [
  'gates', 'lock', 'pre_image', 'build', 'integrity', 'ingest', 'poll', 'import', 'rekey', 'settings', 'id_churn',
];

/** Οι υποχρεωτικές παραδοχές — ανεξάρτητες από το τι γυρίζει ο server. */
const FIXED_ACKS: Array<{ key: string; text: string }> = [
  {
    key: 'irreversible',
    text: 'Καταλαβαίνω ότι η μετάπτωση ΔΕΝ αναιρείται: το μενού του efood αντικαθίσταται ολόκληρο και το '
      + 'προηγούμενο δεν επανέρχεται από εμάς.',
  },
  {
    key: 'codes',
    text: 'Καταλαβαίνω ότι ΟΛΟΙ οι κωδικοί efood (προϊόντων, ομάδων, επιλογών) αλλάζουν. Σταθερός μένει μόνο '
      + 'ο δικός μας κωδικός· οι παλιές αντιστοιχίσεις κωδικού απενεργοποιούνται με αναφορά.',
  },
  {
    key: 'availability',
    text: 'Καταλαβαίνω ότι η διαθεσιμότητα στέλνεται όπως είναι ΣΗΜΕΡΑ στο efood — ποτέ από το POS (υπάρχουν '
      + 'καταστήματα με δεκάδες είδη κλειστά επίτηδες).',
  },
];

const secondsLeft = (expiresAt?: string): number | null => {
  if (!expiresAt) return null;
  const t = new Date(expiresAt).getTime();
  if (!isFinite(t)) return null;
  return Math.round((t - Date.now()) / 1000);
};

const fmtCountdown = (s: number): string => {
  const abs = Math.abs(s);
  const m = Math.floor(abs / 60);
  const sec = abs % 60;
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
};

const fmtBytes = (n: number): string => {
  if (!isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

/** Ό,τι κουβαλά το βήμα πέρα από `step`/`status`, σε μία γραμμή. */
const stepDetails = (step: MigrationStep): string => {
  const keys = Object.keys(step);
  const parts: string[] = [];
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (k === 'step' || k === 'status') continue;
    const v = (step as Record<string, unknown>)[k];
    if (v === null || v === undefined) continue;
    if (typeof v === 'object') continue;
    parts.push(`${k}: ${String(v)}`);
  }
  return parts.join(' · ');
};

const MigrateModal: React.FC<Props> = ({
  storeId, storeName, preview, loadingPreview, running, result, progress = null, error,
  onClose, onGoToHistory, onReloadPreview, onConfirm,
}) => {
  const [acks, setAcks] = useState<Record<string, boolean>>({});
  const [typed, setTyped] = useState('');
  const [showSample, setShowSample] = useState(false);
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!preview || !preview.expires_at) { setLeft(null); return; }
    setLeft(secondsLeft(preview.expires_at));
    const id = window.setInterval(() => setLeft(secondsLeft(preview.expires_at)), 1000);
    return () => window.clearInterval(id);
  }, [preview]);

  // ΓΙΑΤΙ useMemo και όχι σκέτο `preview ? … : []`: ένας νέος άδειος πίνακας σε
  // κάθε render αλλάζει την ταυτότητα της εξάρτησης, οπότε το `requiredAcks`
  // ξαναχτιζόταν συνέχεια — και μαζί του τα κλειδιά των επιβεβαιώσεων.
  const losses = useMemo(() => (preview ? (preview.losses || []) : []), [preview]);
  const blockers = useMemo(() => (preview ? (preview.blockers || []) : []), [preview]);
  const integrity = useMemo(() => (preview ? (preview.integrity || []) : []), [preview]);
  const stats = preview ? preview.stats : null;

  /**
   * Οι υποχρεωτικές επιβεβαιώσεις: οι σταθερές + ΜΙΑ ανά απώλεια που ανέφερε ο
   * server (§13.19.Ε.3). Η απώλεια που δεν τσεκάρεται, δεν στέλνεται.
   */
  const requiredAcks = useMemo(() => {
    const list = FIXED_ACKS.slice();
    for (let i = 0; i < losses.length; i++) {
      list.push({ key: `loss:${losses[i].code}`, text: losses[i].message });
    }
    return list;
  }, [losses]);

  const allAcked = requiredAcks.every(a => !!acks[a.key]);
  const expired = left !== null && left <= 0;
  const alreadyMigrated = !!(preview && preview.migrated_at);
  const nameOk = !!preview && typed.trim() === String(preview.confirm_text_required || storeName).trim();
  const canRun = !!preview && !running && !loadingPreview && allAcked && nameOk
    && !expired && !alreadyMigrated && blockers.length === 0 && integrity.length === 0;

  const steps = result ? (result.steps || []) : [];

  /**
   * ΚΛΕΙΔΩΝΕΙ ΤΟ ΚΛΕΙΣΙΜΟ ΜΟΝΟ ΟΣΟ ΓΝΩΡΙΖΟΥΜΕ ΟΤΙ ΤΡΕΧΕΙ.
   *
   * Από τη Φ5 η μετάπτωση απαντά 202 και το `running` σβήνει ΜΟΝΟ όταν η
   * δημοσκόπηση δει τελική κατάσταση. Με κομμένο δίκτυο (`unreachable`) ή νεκρή
   * διεργασία server (`interrupted`) η τελική κατάσταση δεν έρχεται ΠΟΤΕ — και με
   * `disabled={running}` σε κάθε κουμπί κλεισίματος ολόκληρη η καρτέλα έμενε κλειδωμένη
   * μέχρι το F5. Το κλείσιμο ΔΕΝ ακυρώνει τίποτα: η εκτέλεση ζει στον server.
   */
  const stuck = runWatchStuck(progress);
  const closeLocked = running && !stuck;
  const closeTitle = closeLocked
    ? 'Εκτελείται μετάπτωση — μην κλείσεις'
    : (stuck ? 'Η εκτέλεση συνεχίζει στον server — κλείσε και δες το «Ιστορικό»' : 'Κλείσιμο');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-surface-2 border border-line-strong rounded-2xl w-full max-w-4xl my-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-line">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Μετάπτωση καταλόγου — {storeName}
            </h3>
            <p className="text-xs text-t3 mt-1">
              Ολική αντικατάσταση του μενού του efood με τους δικούς μας κωδικούς. Μία φορά ανά κατάστημα,
              <strong> χωρίς επιστροφή</strong>.
            </p>
            {preview && preview.confirm_hash && (
              <p className="text-[11px] text-t4 mt-1 font-mono break-all">confirm_hash: {preview.confirm_hash}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {left !== null && (
              <span
                className={`text-xs px-2 py-1 rounded-lg ${expired
                  ? 'bg-red-500/15 text-red-700 dark:text-red-300'
                  : 'bg-surface-3 text-t2'}`}
                title="Η προεπισκόπηση δένεται με φρέσκια εικόνα του efood (10΄)"
              >
                {expired ? 'Έληξε' : `Λήγει σε ${fmtCountdown(left)}`}
              </span>
            )}
            <button
              onClick={onClose}
              disabled={closeLocked}
              title={closeTitle}
              className="p-1.5 rounded-lg hover:bg-surface-3 disabled:opacity-40 text-t3"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="p-3 rounded-xl text-sm flex items-start gap-2 border bg-amber-50 dark:bg-amber-900/25 border-amber-300 dark:border-amber-700/50 text-amber-800 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="min-w-0">{error}</span>
            </div>
          )}

          {loadingPreview && !preview && (
            <div className="text-center py-10 text-t3"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          )}

          {alreadyMigrated && (
            <div className="p-3 rounded-xl text-sm border bg-emerald-50 dark:bg-emerald-900/25 border-emerald-300 dark:border-emerald-700/50 text-emerald-800 dark:text-emerald-200">
              Το κατάστημα έχει ήδη μεταπτωθεί ({fmtDateTime(preview && preview.migrated_at)}). Από εδώ και πέρα
              στέλνονται <strong>μόνο αλλαγές</strong> — δεύτερο ingest δεν γίνεται ποτέ.
            </div>
          )}

          {/* Φραγές & ακεραιότητα: ΔΕΝ τσεκάρονται, σταματούν. */}
          {(blockers.length > 0 || integrity.length > 0) && (
            <div className="border border-red-300 dark:border-red-700/50 bg-red-50 dark:bg-red-900/20 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-200">
                <ShieldAlert className="w-4 h-4" /> Η μετάπτωση δεν μπορεί να τρέξει
              </div>
              {blockers.map((b, i) => (
                <p key={`b-${i}`} className="text-xs text-red-900 dark:text-red-100">
                  <span className="font-mono text-[10px] opacity-70">{b.code}</span> {b.message}
                </p>
              ))}
              {integrity.map((msg, i) => (
                <p key={`i-${i}`} className="text-xs text-red-900 dark:text-red-100">
                  <span className="font-mono text-[10px] opacity-70">integrity</span> {msg}
                </p>
              ))}
              <p className="text-[11px] text-red-700 dark:text-red-300">
                Σφάλμα ακεραιότητας σημαίνει ότι <strong>δεν στέλνεται τίποτα</strong>: το σώμα θα δημιουργούσε
                διπλούς ή ορφανούς κωδικούς στο efood.
              </p>
            </div>
          )}

          {/* Τι στέλνεται */}
          {stats && (
            <div className="border border-line rounded-xl p-3 space-y-2">
              <div className="text-sm font-medium text-t1">Τι θα σταλεί</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div><span className="text-t4">Κατηγορίες</span><div className="text-t1 font-semibold">{stats.categories}</div></div>
                <div><span className="text-t4">Είδη</span><div className="text-t1 font-semibold">{stats.products}</div></div>
                <div><span className="text-t4">Ομάδες επιλογών</span><div className="text-t1 font-semibold">{stats.tiers}</div></div>
                <div><span className="text-t4">Επιλογές</span><div className="text-t1 font-semibold">{stats.options}</div></div>
                <div><span className="text-t4">Προσφορές</span><div className="text-t1 font-semibold">{stats.offers}</div></div>
                <div>
                  <span className="text-t4 flex items-center gap-1"><Images className="w-3 h-3" /> Με φωτογραφία</span>
                  <div className="text-t1 font-semibold">{stats.with_images}</div>
                </div>
                <div><span className="text-t4">Μέγεθος σώματος</span><div className="text-t1 font-semibold">{fmtBytes(stats.bytes)}</div></div>
                <div>
                  <span className="text-t4">Εξαιρέσεις χειριστή</span>
                  <div className="text-t1 font-semibold">{stats.excluded_by_operator}</div>
                </div>
              </div>
              <p className="text-[11px] text-t3">
                Τιμές: <strong>{stats.prices_from_catalog}</strong> από τον κατάλογο (delivery) ·{' '}
                <strong>{stats.prices_from_platform}</strong> μένουν όπως είναι στο efood.
                {stats.system_category_skipped && ' Η συστημική κατηγορία «Πιάτα Ημέρας» παραλείπεται (το efood τη φτιάχνει μόνο του· η αποστολή της τη διπλασιάζει).'}
              </p>
              {stats.hidden_categories && stats.hidden_categories.length > 0 && (
                <p className="text-[11px] text-t4">
                  Κρυφές κατηγορίες που μένουν κρυφές: {stats.hidden_categories.join(' · ')}
                </p>
              )}
              {stats.ignore_base_price && stats.ignore_base_price.length > 0 && (
                <p className="text-[11px] text-t4" title="Είδη όπου η τιμή ζει στο μέγεθος (ignore_base_price)">
                  Τιμή από το μέγεθος: {stats.ignore_base_price.join(' · ')}
                </p>
              )}
              <p className="text-[11px] text-t4">
                Οι φωτογραφίες στέλνονται αυτούσιες από το CDN της πλατφόρμας. Μετρήθηκε στο staging:
                130/130 επιβίωσαν (86 URL ίδια, 44 ξαναφιλοξενήθηκαν από το efood).
              </p>
            </div>
          )}

          {/* Δείγμα payload */}
          {preview && preview.preview && (
            <div className="border border-line rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowSample(!showSample)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-surface-1 hover:bg-surface-3 text-left text-sm text-t1"
              >
                {showSample ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Δείγμα του σώματος (οι πρώτες γραμμές — το πλήρες σώμα δεν ταξιδεύει στην οθόνη)
              </button>
              {showSample && (
                <pre className="text-[11px] text-t2 bg-surface-1/60 p-2 overflow-x-auto max-h-64">
                  {JSON.stringify(preview.preview, null, 2)}
                </pre>
              )}
            </div>
          )}

          {/* Απώλειες + υποχρεωτικές παραδοχές */}
          {preview && !result && (
            <div className="border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200">
                <AlertTriangle className="w-4 h-4" /> Τι χάνεται — κάθε γραμμή θέλει ρητή επιβεβαίωση
              </div>
              {requiredAcks.map(a => (
                <label key={a.key} className="flex items-start gap-2 text-xs text-amber-900 dark:text-amber-100">
                  <input
                    type="checkbox"
                    checked={!!acks[a.key]}
                    disabled={running}
                    onChange={e => setAcks({ ...acks, [a.key]: e.target.checked })}
                    className="w-4 h-4 rounded mt-0.5 shrink-0"
                  />
                  <span className="min-w-0">{a.text}</span>
                </label>
              ))}
              {losses.length === 0 && (
                <p className="text-[11px] text-amber-800 dark:text-amber-200">
                  Ο server δεν ανέφερε απώλειες γραμμή-γραμμή. Οι τρεις παραπάνω ισχύουν πάντα.
                </p>
              )}
            </div>
          )}

          {/* Πρόοδος / αποτέλεσμα */}
          {(running || result || progress) && (
            <div className="border border-line rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-t1">
                {running
                  ? <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                  : <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                {/* Όσο δεν ξέρουμε τι γίνεται, το «ΜΗΝ κλείσεις» είναι ψέμα:
                    το κλείσιμο δεν ακυρώνει τίποτα — η εκτέλεση ζει στον server. */}
                {running
                  ? (stuck
                    ? 'Η μετάπτωση συνεχίζει στον server — η οθόνη δεν τη διαβάζει'
                    : 'Εκτελείται μετάπτωση — ΜΗΝ κλείσεις το παράθυρο')
                  : 'Αποτέλεσμα μετάπτωσης'}
              </div>

              {running && (
                <p className="text-xs text-t3">
                  Η κλήση περιλαμβάνει: ingest → αναμονή 6-20΄΄ μέχρι το efood να δείξει τους δικούς μας
                  κωδικούς → νέα εισαγωγή → μεταφορά συνδέσμων. Διαρκεί δεκάδες δευτερόλεπτα. Αν χαθεί η
                  σύνδεση, <strong>μην ξαναπατήσεις</strong>: το ingest μπορεί να έχει ήδη περάσει.
                </p>
              )}

              {/* Η μετάπτωση τρέχει ΣΤΟΝ SERVER: το κλείσιμο του παραθύρου ή ένα
                  κομμένο δίκτυο ΔΕΝ την ακυρώνει και ΔΕΝ είναι αποτυχία. */}
              {progress && (
                <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-300 dark:border-blue-700/50 space-y-1">
                  <p className="text-xs text-blue-900 dark:text-blue-200">
                    Εκτέλεση <strong>#{progress.run_id}</strong> — {runStatusLabel(
                      progress.detail ? progress.detail.status : 'running'
                    )}
                    {progress.detail && progress.detail.halted_reason
                      ? ` (${progress.detail.halted_reason})` : ''}
                    {progress.detail && progress.detail.jobs && progress.detail.jobs.length > 0
                      ? ` · εργασία: ${runStatusLabel(progress.detail.jobs[0].status)}` : ''}
                  </p>
                  {progress.unreachable && (
                    <p className="text-xs text-amber-800 dark:text-amber-200">{progress.unreachable}</p>
                  )}
                  {progress.detail && progress.detail.interrupted && (
                    <p className="text-xs text-red-700 dark:text-red-300">
                      «Εκτελείται» χωρίς καμία ενημέρωση για πάνω από 30΄: πιθανότατα διακόπηκε η διεργασία
                      του server. Το ingest μπορεί να έχει ήδη περάσει — ΜΗΝ ξαναπατήσεις μετάπτωση.
                    </p>
                  )}
                  {!progress.finished && (
                    <p className="text-[11px] text-t4">
                      Η εκτέλεση συνεχίζει στον server ακόμη κι αν κλείσεις την οθόνη· η πλήρης εικόνα
                      είναι στο «Ιστορικό».
                    </p>
                  )}
                </div>
              )}

              {result && steps.length === 0 && (
                <p className="text-[11px] text-t4">
                  Η μετάπτωση απαντά 202 και τρέχει στο παρασκήνιο: η λίστα είναι τα βήματα
                  που θα γίνουν, όχι αυτά που έγιναν. Η αληθινή εικόνα είναι παραπάνω
                  (κατάσταση εκτέλεσης) και στο «Ιστορικό».
                </p>
              )}

              <ul className="space-y-1">
                {(steps.length > 0 ? steps.map(s => s.step) : STEP_ORDER).map((name, i) => {
                  const step = steps.filter(s => s.step === name)[0];
                  const status = step ? step.status : null;
                  return (
                    <li key={`${name}-${i}`} className="flex items-start gap-2 text-xs">
                      <span className={`mt-0.5 w-3.5 h-3.5 rounded-full shrink-0 ${
                        status === 'ok' ? 'bg-emerald-500'
                          : status === 'failed' ? 'bg-red-500'
                            : status === 'skipped' ? 'bg-slate-400'
                              : 'bg-surface-3 border border-line-strong'}`} />
                      <span className="min-w-0">
                        <span className={status === 'failed' ? 'text-red-700 dark:text-red-300' : 'text-t2'}>
                          {STEP_LABELS[name] || name}
                        </span>
                        {step && (
                          <span className="block text-[10px] text-t4 font-mono break-all">{stepDetails(step)}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {result && result.status === 'halted' && (
                <div className="p-2 rounded-lg bg-red-500/10 border border-red-300 dark:border-red-700/50 text-xs text-red-800 dark:text-red-200">
                  <strong>Σταμάτησε μετά το ingest.</strong> Το μενού του efood έχει ΗΔΗ αντικατασταθεί, αλλά
                  κάποιο επόμενο βήμα απέτυχε. <strong>Δεύτερο ingest δεν γίνεται αυτόματα — και δεν πρέπει να
                  γίνει χειροκίνητα χωρίς έλεγχο.</strong> {result.error || ''}
                </div>
              )}

              {result && result.status === 'done' && (
                <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-300 dark:border-emerald-700/50 text-xs text-emerald-800 dark:text-emerald-200">
                  Ολοκληρώθηκε. Το κατάστημα απευθύνεται πλέον με τους δικούς μας κωδικούς: από εδώ και πέρα
                  στέλνονται <strong>μόνο αλλαγές</strong>.
                </div>
              )}

              {result && result.rekey && (
                <p className="text-[11px] text-t3">
                  Re-key: <strong>{result.rekey.status === 'ok' ? 'ok' : 'θέλει ανθρώπινο έλεγχο'}</strong> ·
                  {' '}αντιστοιχίστηκαν {result.rekey.mapped}
                  {result.rekey.links_moved !== undefined ? ` · σύνδεσμοι ${result.rekey.links_moved}` : ''}
                  {result.rekey.twins_moved !== undefined ? ` · δίδυμα ${result.rekey.twins_moved}` : ''}
                  {result.rekey.edits_moved !== undefined ? ` · πρόχειρα ${result.rekey.edits_moved}` : ''}
                  {result.rekey.orphans && result.rekey.orphans.length > 0
                    ? ` · ΟΡΦΑΝΑ ${result.rekey.orphans.length} (καμία εγγραφή δεν έγινε)` : ''}
                  {result.rekey.unmatched_new && result.rekey.unmatched_new.length > 0
                    ? ` · άγνωστα νέα ${result.rekey.unmatched_new.length}` : ''}
                  {result.rekey.tier_gaps ? ` · κενά ομάδων ${result.rekey.tier_gaps}` : ''}
                  {result.rekey.option_gaps ? ` · κενά επιλογών ${result.rekey.option_gaps}` : ''}
                </p>
              )}

              {result && result.error && result.status !== 'halted' && (
                <p className="text-xs text-red-700 dark:text-red-300">{result.error}</p>
              )}
            </div>
          )}
        </div>

        {/* Υποσέλιδο: πληκτρολόγηση ονόματος + εκτέλεση */}
        <div className="p-4 border-t border-line space-y-2">
          {!result && (
            <>
              <label className="block text-xs text-t3">
                Πληκτρολόγησε το όνομα του καταστήματος για επιβεβαίωση:{' '}
                <strong className="text-t1">{preview ? preview.confirm_text_required : storeName}</strong>
              </label>
              <input
                type="text"
                value={typed}
                disabled={running || !preview}
                onChange={e => setTyped(e.target.value)}
                placeholder={preview ? preview.confirm_text_required : storeName}
                className={`w-full sm:w-96 bg-surface-0 border rounded-lg px-3 py-2 text-sm text-t1 disabled:opacity-50 ${
                  typed && !nameOk ? 'border-red-500' : 'border-line-strong'}`}
              />
            </>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-t3">
              {result
                ? `Εκτέλεση #${result.run_id}`
                  + (result.job_id ? ` · εργασία #${result.job_id}` : '')
                  + ` · κατ. ${storeId}`
                : `${requiredAcks.filter(a => !!acks[a.key]).length}/${requiredAcks.length} επιβεβαιώσεις`}
            </span>
            <div className="ml-auto flex items-center gap-2">
              {!result && (
                <button
                  type="button"
                  onClick={onReloadPreview}
                  disabled={running || loadingPreview}
                  title="Ξαναϋπολογίζει την προεπισκόπηση με φρέσκια εικόνα"
                  className="px-3 py-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl text-sm"
                >
                  Νέα προεπισκόπηση
                </button>
              )}
              {progress && onGoToHistory && (
                <button
                  type="button"
                  onClick={onGoToHistory}
                  title="Η εκτέλεση συνεχίζει στον server — εκεί φαίνεται όλη η εικόνα"
                  className="px-3 py-2 bg-surface-2 hover:bg-surface-3 text-t1 border border-line-strong rounded-xl text-sm"
                >
                  Δες το Ιστορικό
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={closeLocked}
                title={closeTitle}
                className="px-3 py-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl text-sm"
              >
                Κλείσιμο
              </button>
              {!result && (
                <button
                  type="button"
                  onClick={() => onConfirm(typed.trim())}
                  disabled={!canRun}
                  title={canRun
                    ? 'Στέλνει ΟΛΟΚΛΗΡΟ τον κατάλογο στο efood — δεν αναιρείται'
                    : 'Χρειάζονται: καμία φραγή, όλες οι επιβεβαιώσεις, σωστό όνομα καταστήματος και μη ληγμένη προεπισκόπηση'}
                  className="px-3.5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2"
                >
                  {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  Μετάπτωση τώρα
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigrateModal;
